// utils/push.js — Web Push (การแจ้งเตือนที่เด้งขึ้นจอแม้ไม่ได้เปิดเว็บ)
//
// ชั้นนี้เป็น "ของแถม" ไม่ใช่ชั้นหลัก — กล่องกระดิ่งในเว็บทำงานได้ทุกเบราว์เซอร์เสมอ
// ส่วน push มีเงื่อนไขเยอะและผู้ใช้ปฏิเสธได้ จึงต้องพังแบบเงียบและบอกเหตุผลให้ผู้ใช้เข้าใจ
//
// เงื่อนไขที่ต้องผ่านครบถึงจะใช้ได้:
//   1. เบราว์เซอร์รองรับ serviceWorker + PushManager + Notification
//   2. หน้าอยู่บน HTTPS (หรือ localhost) — prod และ dev เป็น https อยู่แล้ว
//   3. ผู้ใช้กดอนุญาต (permission = 'granted')
//   4. ⚠️ บน iOS/iPadOS: ต้องติดตั้งเป็น PWA ("เพิ่มไปยังหน้าจอโฮม") ก่อนเท่านั้น
//      Safari ในแท็บปกติจะไม่มี PushManager ให้เลย — ตรวจด้วย isIos() + isStandalone()
//      เพื่อบอกผู้ใช้ให้ถูกเรื่อง แทนที่จะขึ้น "เบราว์เซอร์ไม่รองรับ" ซึ่งไม่ช่วยให้เขาทำอะไรต่อได้

// VAPID public key ที่ backend ส่งมาเป็น base64url — PushManager ต้องการ Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function isIos() {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ รายงานตัวเองเป็น Mac — แยกด้วยการมี touch point
  return /iPad|iPhone|iPod/.test(ua)
    || (/Macintosh/.test(ua) && typeof document !== 'undefined' && navigator.maxTouchPoints > 1);
}

// ติดตั้งเป็น PWA แล้วหรือยัง (เปิดจากไอคอนบนหน้าจอโฮม ไม่ใช่ในแท็บเบราว์เซอร์)
export function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// สถานะที่หน้าจอใช้ตัดสินว่าจะโชว์อะไร:
//   'unsupported'  เบราว์เซอร์ไม่รองรับเลย
//   'ios-install'  เป็น iOS แต่ยังไม่ได้ติดตั้ง PWA → บอกวิธีเพิ่มไปหน้าจอโฮม
//   'blocked'      ผู้ใช้เคยกดปฏิเสธ → ต้องไปแก้ในตั้งค่าเบราว์เซอร์เอง (เรียกขอซ้ำไม่ขึ้นแล้ว)
//   'on' / 'off'   พร้อมใช้งาน
export async function pushState() {
  if (isIos() && !isStandalone()) return 'ios-install';
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    return sub ? 'on' : 'off';
  } catch (e) {
    return 'off';
  }
}

// ลงทะเบียน service worker — updateViaCache:'none' กันไม่ให้ตัว sw.js เองติดแคช HTTP
// (CDN ของ DO แคชได้ถึง 24 ชม. ถ้าติดแคชจะแก้ push logic แล้วไม่ไปถึงเครื่องผู้ใช้)
//
// ⚠️ ห้ามใช้ `process.env.*` ในโปรเจกต์นี้เด็ดขาด — **`process` ไม่มีอยู่จริงในบันเดิล**
// vue.config.js ประกาศ DefinePlugin เป็น `'env'` เฉย ๆ (ทั้งแอปจึงเรียก `env.SERVER_URL`
// ไม่ใช่ `process.env.SERVER_URL`) เขียน process.env ที่นี่จะได้ ReferenceError ตอนรันจริง
// และ **eslint จับไม่ได้** เพราะ eslintConfig ตั้ง `env: { node: true }` ไว้ มันเลยคิดว่า
// process เป็นของถูกต้อง — บั๊กนี้เคยหลุดไปแล้วรอบหนึ่ง ทำให้ปุ่มเปิดการแจ้งเตือนพังเงียบ ๆ
// โดยที่ lint ผ่าน 100% (เจอตอนเจ้าของลองกดจริงบน Chrome เท่านั้น)
//
// ใช้ document.baseURI แทน — ได้ path ที่ถูกต้องทั้งตอน deploy ที่ราก และใต้ sub-path
export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) {
    // มีอยู่แล้วก็ยังต้องสั่งเช็คของใหม่ — ดูเหตุผลยาวที่ refreshServiceWorker()
    existing.update().catch(() => { });
    return existing;
  }
  const swUrl = new URL('sw.js', document.baseURI).href;
  return navigator.serviceWorker.register(swUrl, { updateViaCache: 'none' });
}

// ── บังคับให้ service worker เช็คสคริปต์ใหม่ทุกครั้งที่เปิดแอป ────────────────
//
// ⚠️ ถ้าไม่มีตัวนี้ การแก้ sw.js จะไม่มีวันไปถึงเครื่องที่ "เคยสมัครไปแล้ว"
// เบราว์เซอร์ยึด SW ตัวที่ติดตั้งไว้ต่อไปเรื่อย ๆ และไปเช็คของใหม่เองราว 24 ชม.ครั้ง
// = deploy แก้บั๊กของ sw.js แล้วโค้ชอาจได้ของใหม่วันรุ่งขึ้น โดยไม่มีใครรู้ว่าทำไมยังพังอยู่
// (เจอจริงตอนเทสต์: แก้ให้ sw.js ส่งวันของนัดหมายมาแล้ว แต่เครื่องที่สมัครไว้ก่อนหน้า
//  ยังรัน SW ตัวเก่าที่ไม่มีโค้ดนั้น กดแล้วโฟกัสแท็บให้แต่ไม่เปลี่ยนวัน)
//
// คู่กับ skipWaiting() + clients.claim() ใน sw.js: พอเจอของใหม่ ตัวใหม่จะขึ้นมาแทนทันที
// ไม่ต้องรอปิดแท็บทุกบาน · เป็น network request เล็ก ๆ ครั้งเดียวตอนบูต คุ้มกับการที่แก้แล้วถึงมือจริง
export async function refreshServiceWorker() {
  try {
    if (!('serviceWorker' in navigator)) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;      // ยังไม่เคยสมัคร = ไม่ต้องทำอะไร (อย่าไปสมัครให้เอง)
    await reg.update();
    return true;
  } catch (e) {
    return false;
  }
}

// เปิดการแจ้งเตือน: ขอสิทธิ์ → subscribe → ส่ง subscription ให้ backend เก็บ
// คืน { ok, reason } — reason ใช้เลือกข้อความ i18n ที่ตรงกับสาเหตุจริง
export async function enablePush({ publicKey, save }) {
  if (isIos() && !isStandalone()) return { ok: false, reason: 'ios-install' };
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!publicKey) return { ok: false, reason: 'fail' };

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm === 'denied' ? 'blocked' : 'fail' };

  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: 'unsupported' };
  await navigator.serviceWorker.ready;

  // ถ้าเคย subscribe ไว้ด้วยคีย์ชุดเก่า (เปลี่ยน VAPID key) ต้องยกเลิกก่อน ไม่งั้น subscribe
  // จะ throw InvalidStateError แล้วผู้ใช้จะเปิดไม่ได้ตลอดกาลโดยไม่รู้สาเหตุ
  const old = await reg.pushManager.getSubscription();
  if (old) {
    const sameKey = old.options && old.options.applicationServerKey
      && btoa(String.fromCharCode(...new Uint8Array(old.options.applicationServerKey)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') === publicKey;
    if (!sameKey) await old.unsubscribe();
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  const json = sub.toJSON();
  await save({ endpoint: sub.endpoint, p256dh: json.keys.p256dh, authkey: json.keys.auth });
  return { ok: true };
}

// ปิดการแจ้งเตือนบนเครื่องนี้ — ยกเลิกฝั่งเบราว์เซอร์ "และ" ลบแถวฝั่ง backend
// (ลบแค่ฝั่งเดียวแล้วอีกฝั่งจะค้าง: ยกเลิกแต่ไม่ลบ = backend ยิงไปที่ endpoint ผี ·
//  ลบแต่ไม่ยกเลิก = กดเปิดใหม่แล้วได้ subscription เดิมที่ backend ไม่รู้จัก)
export async function disablePush({ remove }) {
  try {
    if (!('serviceWorker' in navigator)) return { ok: true };
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (!sub) return { ok: true };
    const { endpoint } = sub;

    // ⚠️ ลำดับสำคัญ: ยกเลิกฝั่งเบราว์เซอร์ "ก่อน" เสมอ
    // นี่คือขั้นเดียวที่รับประกันว่าจะไม่มีการแจ้งเตือนเด้งขึ้นจออีก (push service เลิกรู้จัก
    // เครื่องนี้ทันที) ถ้าเรียก backend ก่อนแล้วเน็ตหลุด — ซึ่งเกิดง่ายมากตอนกดออกจากระบบ
    // เพราะคนมักกดตอนกำลังจะเดินออกจากที่ — โค้ดจะ throw ออกไปโดยที่ยังไม่ได้ยกเลิกเลย
    // ผลคือ "กดออกจากระบบแล้วแต่ยังได้แจ้งเตือนอยู่" ซึ่งคือสิ่งที่เรากำลังแก้พอดี
    await sub.unsubscribe();

    // แจ้ง backend เป็น best-effort — ล้มเหลวก็ไม่เป็นไร แถวที่ค้างจะหายเองรอบส่งถัดไป
    // (push service ตอบ 404/410 เพราะ subscription ถูกยกเลิกไปแล้ว → notify.js ลบแถวทิ้งอัตโนมัติ)
    try { await remove(endpoint); } catch (e) { /* self-heal ตามที่อธิบายข้างบน */ }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'fail' };
  }
}

// ── ผูก subscription ที่มีอยู่แล้วเข้ากับ "คนที่กำลังล็อกอินอยู่ตอนนี้" ────────────
//
// ทำไมต้องมี: subscription ของ Web Push อยู่ที่ "เบราว์เซอร์" ไม่ใช่ที่ session ของเรา
// มันไม่รู้จักและไม่หมดอายุตาม JWT เลย → เครื่องที่เปลี่ยนมือ (โค้ช A เปิดไว้ แล้วโค้ช B
// มาล็อกอินต่อโดยที่ A ไม่ได้กด logout) จะทำให้ B ถือเครื่องที่ยังเด้งแจ้งเตือนของ A อยู่
//
// เรียกหลังล็อกอินสำเร็จทุกครั้ง: ถ้าเบราว์เซอร์นี้เคยเปิดแจ้งเตือนไว้ ให้ส่ง subscription
// เดิมกลับไปบันทึกใหม่ — endpoint เป็น UNIQUE KEY ฝั่ง DB และ upsert เขียนทับ username
// จึงเท่ากับ "ย้ายเจ้าของ" ให้คนที่ล็อกอินอยู่จริง โดยไม่ต้องขอสิทธิ์จากผู้ใช้ใหม่
// (ไม่มี subscription = ไม่ทำอะไรเลย ไม่ไปขอสิทธิ์แจ้งเตือนเองโดยที่ผู้ใช้ไม่ได้สั่ง)
export async function rebindPushToCurrentUser({ save }) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (!sub) return false;
    const json = sub.toJSON();
    if (!json.keys || !json.keys.p256dh || !json.keys.auth) return false;
    await save({ endpoint: sub.endpoint, p256dh: json.keys.p256dh, authkey: json.keys.auth });
    return true;
  } catch (e) {
    return false;
  }
}
